import {logger} from 'yougile-util'
import {
  DataObjectId,
  GroupChat,
  groupChatContainerId,
  Hub,
  HubData,
  newsCompanyId,
  StorageDepartmentHierarchy,
  User
} from 'yougile-model'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {TrackingEvent} from '@common/tracking-events'
import {Tracking} from '../../common/tracking'
import {DepartmentsClient} from '../client/departments-client'
import {DirectMessages} from '../client/direct-messages'
import {PermissionsChecker} from '../client/permissions-checker'
import {Sync} from '../client/sync'
import {globals} from './globals-base'
import {SystemEntities} from './system-entities'

/**
 * Allow to check who is subscribed for chat notifications and to modify it.
 * Stateless class.
 */
export class ChatSubscribers {
  static setSubscribedToTaskChat(
    taskId: DataObjectId,
    userIds: DataObjectId[]
  ): void {
    const box = Sync.getCurrentSandbox()
    const notifyTag = SystemEntities.get(box.companyId).notifyTagId
    if (!box.isConnected(notifyTag, taskId)) {
      box.connect(notifyTag, taskId)
    }
    box.setModifiers(notifyTag, taskId, userIds)
  }

  public static getSubscribedUserIds(chatHub: Hub): DataObjectId[] {
    if (!chatHub || chatHub.companyId === newsCompanyId) {
      return []
    }
    if (chatHub.location === groupChatContainerId) {
      const participants = GroupChat.getParticipants(chatHub)
      return Object.keys(participants).filter(uid => {
        return !!uid && participants[uid]
      }) as DataObjectId[]
    }
    const res: DataObjectId[] = []
    if (chatHub.companyId === Sync.profileId) {
      const u2 = DirectMessages.getDirectChatUser(chatHub)
      if (u2) {
        res.push(u2.id)
      }
      res.push(globals.currentUserId)
      return res
    } else {
      const sb = Sync.get(chatHub)
      const notifyTag = SystemEntities.get(chatHub.companyId).notifyTagId
      if (!sb.hasModifiers(notifyTag, chatHub.id)) {
        return []
      } else {
        return sb.getModifiers(notifyTag, chatHub.id) as DataObjectId[]
      }
    }
  }

  public static isSubscribed(
    userId: DataObjectId | undefined,
    obj: {id: DataObjectId; companyId: DataObjectId} | undefined
  ): boolean {
    if (!obj || !userId) {
      return false
    }
    const {id, companyId} = obj
    const box = Sync.get({companyId})
    if (!box) {
      return false
    }
    const chatHub = box.get(id) as Hub
    if (!chatHub) {
      return false
    }
    if (companyId === newsCompanyId) {
      return false
    }

    if (chatHub.location === groupChatContainerId) {
      return GroupChat.isNotified(chatHub, userId)
    }

    if (chatHub.companyId === Sync.profileId) {
      if (userId === globals.currentUserId) {
        return true
      }
      const user = DirectMessages.getDirectChatUser(chatHub)
      return user && user.id === userId
    } else {
      const sb = Sync.get(chatHub)
      const user = sb.get(userId) as User
      if (!user) {
        return false
      }
      const system = SystemEntities.get(user.companyId)
      if (!system) {
        return false
      }
      const notifyTag = system.notifyTagId
      if (!sb.hasModifiers(notifyTag, chatHub.id)) {
        return false
      } else {
        return sb.getModifiers(notifyTag, chatHub.id).includes(user.id)
      }
    }
  }

  public static subscribe(
    userId: DataObjectId | undefined,
    obj:
      | {id: DataObjectId; companyId: DataObjectId; data?: HubData}
      | undefined,
    commit = true
  ): void {
    if (!obj || !userId) {
      return
    }
    if (SharedBoardsClient.getId()) {
      return
    }
    const {id, companyId} = obj
    const box = Sync.get({companyId})
    if (!box) {
      return
    }
    const chatHub = box.get(id) as Hub
    if (!chatHub) {
      return
    }
    if (chatHub.location === groupChatContainerId) {
      return
    }

    const system = SystemEntities.get(chatHub.companyId)
    if (!system) {
      return
    }
    const notifyTag = system.notifyTagId
    if (ChatSubscribers.isSubscribed(userId, chatHub)) {
      logger.warn('User already subscribed')
    } else {
      if (!box.isConnected(notifyTag, chatHub.id)) {
        box.connect(notifyTag, chatHub.id)
      }

      box.addModifiers(notifyTag, chatHub.id, [userId], commit)
      if (!obj.data?.isCreatedByDefault) {
        void Tracking.send(TrackingEvent.chatChangeWhoNotified)
      }
    }
  }

  public static unsubscribe(
    userId: DataObjectId,
    obj: {id: DataObjectId; companyId: DataObjectId}
  ): void {
    if (!obj) {
      return
    }
    const {id, companyId} = obj
    const box = Sync.get({companyId})
    if (!box) {
      return
    }
    const chatHub = box.get(id) as Hub
    if (!chatHub) {
      return
    }
    if (chatHub.location === groupChatContainerId) {
      return
    }
    const system = SystemEntities.get(chatHub.companyId)
    if (!system) {
      return
    }
    const notifyTag = system.notifyTagId

    if (ChatSubscribers.isSubscribed(userId, chatHub)) {
      box.removeModifiers(notifyTag, chatHub.id, [userId])
    } else {
      throw new Error('User is not subscribed')
    }
    void Tracking.send(TrackingEvent.chatChangeWhoNotified)
  }

  public static safeSubscribe(
    userId: DataObjectId,
    obj: {id: DataObjectId; companyId: DataObjectId} | undefined
  ): void {
    if (!obj) {
      return
    }
    const {isSubscribed, subscribe} = ChatSubscribers

    if (!isSubscribed(userId, obj)) {
      subscribe(userId, obj)
    }
  }

  public static safeUnsubscribe(
    userId: DataObjectId,
    obj: {id: DataObjectId; companyId: DataObjectId}
  ): void {
    const {isSubscribed, unsubscribe} = ChatSubscribers

    if (isSubscribed(userId, obj)) {
      unsubscribe(userId, obj)
    }
  }

  public static setSubscription(
    userId: DataObjectId,
    obj: {id: DataObjectId; companyId: DataObjectId; data: HubData},
    isSubscribed: boolean,
    commit = true
  ): void {
    const wasSubscribed = ChatSubscribers.isSubscribed(userId, obj)

    if (isSubscribed && !wasSubscribed) {
      ChatSubscribers.subscribe(userId, obj, commit)
    } else if (!isSubscribed && wasSubscribed) {
      ChatSubscribers.unsubscribe(userId, obj)
    }
  }

  public static toggleSubscribe(userId: DataObjectId, chatHub: Hub): void {
    ChatSubscribers.setSubscription(
      userId,
      chatHub,
      !ChatSubscribers.isSubscribed(userId, chatHub)
    )
  }

  public static canBeSubscribed(
    userId: DataObjectId,
    chatHub: Hub | undefined,
    hierarchy?: StorageDepartmentHierarchy
  ): boolean {
    const calculatedHierarchy =
      hierarchy ?? DepartmentsClient.getHierarchy(Sync.getCurrentSandbox())
    return PermissionsChecker.checkShow('ignore-departments', chatHub, {
      considerAsWithMe: true,
      userId,
      ignoreAdmin: true,
      hasNoChildrenHint: true,
      checkCanAssignUser: true,
      calculatedHierarchy
    })
  }
}
